import { Fragment, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api/client';
import { StorageTiers } from './StorageTiers';
import { CardConversionCredits } from './CardConversionCredits';
import { ConfirmDialog } from '../../components/ConfirmDialog';

export interface Subscription {
  subscriptionId: string;
  planName: string | null;
  captureType: string | null;
  billingMode: string;
  quantity: number;
  status: string;
  priceId: string | null;
  family: string | null;
  planType: string | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  pending: { priceId: string; planName: string | null } | null;
  includedSeats: number;
  additionalSeats: number;
  pendingAdditional: number | null;
  breakdown: {
    baseAmount: number; interval: string; includedSeats: number;
    additionalCount: number; additionalUnitAmount: number | null; currency: string; total: number;
  } | null;
}

interface OpenInvoice {
  id: string;
  number: string | null;
  amountDueCents: number;
  currency: string;
  created: number;
  hostedInvoiceUrl: string | null;
  status: string | null;
}

/** A rate band of a volume-priced plan. `upTo` is null on the final open-ended band. */
interface PlanTier {
  upTo: number | null;
  unitAmount: number;
}

export interface Plan {
  priceId: string;
  productName: string;
  description: string | null;
  /** Entry rate. For a volume-tiered plan this is the first tier, not a flat price — see `tiers`. */
  amount: number;
  /** Present only for volume-tiered plans (the pay-per-transaction ones). */
  tiers?: PlanTier[];
  /** Cost of each scanner beyond the plan's included seat, for licensed plans. */
  additionalDeviceAmount?: number;
  /** Devices covered by the base price (v2 licensed plans include one). */
  includedSeats?: string | null;
  currency: string;
  interval: string;
  usageType: string;
  feature: string | null;
  captureType: string | null;
  planType: string | null;
  family: string | null;
  rank: number | null;
}

function money(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
}
function fmtDate(epoch: number | null) {
  return epoch ? new Date(epoch * 1000).toLocaleDateString() : '';
}

/**
 * One-line rate for a plan, used wherever a plan is named inline (e.g. change-plan buttons).
 *
 * Usage plans are quoted per transaction with NO interval: they carry no recurring fee, so "$50/month
 * per transaction" wrongly implies a monthly charge. Volume-tiered plans quote their entry rate as a
 * "from" figure, since the rate falls with monthly volume.
 */
/**
 * What the next invoice will be, when that differs from what is being billed now.
 *
 * Scheduled changes are visible on the page ("switches to X", "scanners drop to 1") but their effect on
 * the amount was not, so a customer could see a $125/month total, a queued reduction, and no idea what
 * they would actually be charged at renewal.
 *
 * Returns null when the renewal is the same as today, or when there is nothing meaningful to quote — a
 * cancellation (the cancel notice already says it ends) or a usage plan with no recurring figure.
 */
export function renewalOf(s: Subscription, plans: Plan[]): { amount: number; interval: string; note?: string } | null {
  if (s.cancelAtPeriodEnd) return null;

  // A queued plan switch: /subscriptions/change writes a single-item phase, so add-on scanners do not
  // carry across — the renewal is the target plan's own price.
  if (s.pending) {
    const target = plans.find((p) => p.priceId === s.pending!.priceId);
    if (!target) return null;
    if (target.usageType === 'metered') return null; // billed per transaction; no fixed renewal to quote
    const note = s.breakdown && s.breakdown.additionalCount > 0
      ? `${s.breakdown.additionalCount} additional scanner${s.breakdown.additionalCount === 1 ? '' : 's'} do not carry over`
      : undefined;
    return { amount: target.amount, interval: target.interval, ...(note ? { note } : {}) };
  }

  // A queued scanner reduction on the same plan.
  const b = s.breakdown;
  if (b && s.pendingAdditional != null && s.pendingAdditional !== b.additionalCount) {
    const amount = b.baseAmount + s.pendingAdditional * (b.additionalUnitAmount ?? 0);
    if (amount === b.total) return null;
    return {
      amount,
      interval: b.interval,
      note: `${s.pendingAdditional} additional scanner${s.pendingAdditional === 1 ? '' : 's'}`,
    };
  }
  return null;
}

function rateLabel(p: Plan) {
  if (p.usageType === 'metered') return `${p.tiers ? 'from ' : ''}${money(p.amount, p.currency)}/tx`;
  return `${money(p.amount, p.currency)}/${p.interval}`;
}

/**
 * What a licensed plan covers and what extra scanners cost.
 *
 * v2 unlimited plans include ONE device; v1 sold unlimited devices on the base price, so stating the
 * included count and the add-on rate is the difference a migrating customer needs to see.
 */
function DeviceNote({ plan }: { plan: Plan }) {
  const seats = Number(plan.includedSeats ?? 1) || 1;
  return (
    <p className="plan-seats">
      Unlimited transactions · includes {seats} {seats === 1 ? 'device' : 'devices'}
      {plan.additionalDeviceAmount
        ? ` · additional devices ${money(plan.additionalDeviceAmount, plan.currency)}/${plan.interval} each`
        : ''}
    </p>
  );
}

/**
 * The volume-discount table for a tiered plan, collapsed by default.
 *
 * Stripe expresses tiers as upper bounds only, so each band's lower bound is the previous band's
 * `upTo` + 1. `tiers_mode: volume` means the customer's TOTAL monthly count picks one band and every
 * transaction that month bills at that band's rate — it is not a graduated/stacked calculation.
 */
function TierTable({ tiers, currency }: { tiers: PlanTier[]; currency: string }) {
  const lowest = tiers[tiers.length - 1];
  return (
    <details className="plan-tiers">
      <summary>Volume pricing — as low as {money(lowest?.unitAmount ?? 0, currency)}/tx</summary>
      <ul>
        {tiers.map((t, i) => {
          const from = i === 0 ? 1 : (tiers[i - 1]?.upTo ?? 0) + 1;
          const range = t.upTo === null ? `${from}+` : from >= t.upTo ? `${t.upTo}` : `${from}–${t.upTo}`;
          return (
            <li key={t.upTo ?? 'max'}>
              <span>{range}/month</span>
              <strong>{money(t.unitAmount, currency)}</strong>
            </li>
          );
        })}
      </ul>
      <p className="plan-tiers-note">Your monthly total sets the rate for every transaction that month.</p>
    </details>
  );
}

const STATUS_CLASS: Record<string, string> = {
  ACTIVE: 'ok', PAST_DUE: 'warn', CANCELED: 'muted', INCOMPLETE: 'warn',
};

export function Subscriptions() {
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const subs = useQuery({ queryKey: ['subscriptions'], queryFn: () => api.get<Subscription[]>('/subscriptions') });
  const plans = useQuery({ queryKey: ['plans'], queryFn: () => api.get<Plan[]>('/plans') });
  const invoices = useQuery({ queryKey: ['open-invoices'], queryFn: () => api.get<OpenInvoice[]>('/billing/invoices') });
  const [busy, setBusy] = useState<string | null>(null);
  /** Subscription whose detail row is open, or null. One at a time keeps the table compact. */
  const [expanded, setExpanded] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [addConfirm, setAddConfirm] = useState<Subscription | null>(null);

  const refetch = () => qc.invalidateQueries({ queryKey: ['subscriptions'] });

  async function manageBilling() {
    setBusy('portal'); setErr(null);
    try {
      const res = await api.post<{ url: string }>('/billing/portal', {});
      if (res.url) window.location.href = res.url;
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not open the billing portal');
    } finally { setBusy(null); }
  }

  async function subscribe(priceId: string) {
    setBusy(priceId); setErr(null);
    try {
      const res = await api.post<{ checkoutUrl: string }>('/subscriptions/checkout', { priceId });
      if (res.checkoutUrl) window.location.href = res.checkoutUrl;
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not start checkout');
    } finally { setBusy(null); }
  }

  async function changePlan(subscriptionId: string, priceId: string, key: string) {
    setBusy(key); setErr(null);
    try {
      await api.post('/subscriptions/change', { subscriptionId, priceId });
      refetch();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not change plan');
    } finally { setBusy(null); }
  }

  async function setCancel(subscriptionId: string, cancel: boolean) {
    setBusy(`cancel-${subscriptionId}`); setErr(null);
    try {
      await api.post('/subscriptions/cancel', { subscriptionId, cancel });
      refetch();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not update the subscription');
    } finally { setBusy(null); }
  }

  async function setScanners(additional: number) {
    setBusy('scanners'); setErr(null);
    try {
      await api.post('/licenses/scanners', { additional: Math.max(0, additional) });
      refetch();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not change scanners');
    } finally { setBusy(null); }
  }

  const allPlans = plans.data ?? [];
  const activeSubs = (subs.data ?? []).filter((s) => s.status !== 'CANCELED');
  const currentPriceIds = new Set(activeSubs.map((s) => s.priceId).filter(Boolean));
  const coveredFamilies = new Set(activeSubs.map((s) => s.family).filter(Boolean));

  // Plans available as a NEW subscription: not already held, and not in a family already subscribed to.
  const availablePlans = allPlans.filter(
    (p) => !currentPriceIds.has(p.priceId) && !(p.family && coveredFamilies.has(p.family)),
  );

  const suspended = (subs.data ?? []).some((s) => s.status === 'PAST_DUE');
  const openInvoices = invoices.data ?? [];

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Subscriptions &amp; billing</h1>
          <div className="sub">Add, change, or cancel plans. Payment methods &amp; invoices are managed in Stripe.</div>
        </div>
        <button className="btn btn-ghost" onClick={manageBilling} disabled={busy === 'portal'}>
          {busy === 'portal' ? 'Opening…' : 'Payment methods & invoices →'}
        </button>
      </div>

      {(suspended || openInvoices.length > 0) && (
        <div className="banner-warn" style={{ marginBottom: 16 }}>
          <strong>{suspended ? 'Subscription suspended — payment required.' : 'You have an unpaid invoice.'}</strong>
          <div style={{ marginTop: 4, fontSize: 14 }}>
            {suspended
              ? 'Capturing and converting are paused until your outstanding invoice is paid. Your subscription reactivates automatically once payment succeeds.'
              : 'Pay your outstanding balance to keep your subscription active.'}
          </div>
          {openInvoices.length > 0 && (
            <ul className="invoice-list" style={{ listStyle: 'none', padding: 0, margin: '12px 0 0' }}>
              {openInvoices.map((inv) => (
                <li key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                  <span>
                    Invoice {inv.number ?? inv.id} — <strong>{money(inv.amountDueCents, inv.currency)}</strong>
                  </span>
                  {inv.hostedInvoiceUrl && (
                    <a className="btn btn-primary btn-sm" href={inv.hostedInvoiceUrl} target="_blank" rel="noreferrer">Pay now →</a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {params.get('subscribed') && (
        <div className="banner-ok">✓ Thanks! Your subscription is being activated — it will appear shortly.</div>
      )}
      {params.get('credits') && (
        <div className="banner-ok">✓ Thanks! Your card-conversion credits have been added to your account.</div>
      )}
      {err && <p className="cart-error">{err}</p>}

      <div className="section-title">Your subscriptions</div>
      {subs.isLoading ? (
        <p className="loading-screen">// loading subscriptions…</p>
      ) : activeSubs.length === 0 ? (
        <div className="panel empty"><h3>No active subscriptions</h3><p>Choose a plan below to get started.</p></div>
      ) : (
        /*
         * One panel, one row per subscription. This was a full-height card per plan, which pushed the
         * available-plans catalog off-screen for anyone holding two or three subscriptions. The per-plan
         * detail — cost breakdown, scanner controls, change/cancel actions — moves into a row that
         * expands on demand, so the common case (checking what you are on and when it renews) is a
         * glance rather than a scroll.
         */
        <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="tx-table sub-table">
            <thead>
              <tr><th>Plan</th><th>Billing</th><th>Renews</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {activeSubs.map((s) => {
                const targets = allPlans.filter((p) => p.family && p.family === s.family && p.priceId !== s.priceId);
                const open = expanded === s.subscriptionId;
                const renewal = renewalOf(s, allPlans);
                // A row with something to act on or explain is worth opening; a plain metered plan is not.
                const hasDetail = Boolean(s.breakdown) || targets.length > 0 || !s.cancelAtPeriodEnd || Boolean(s.pending);
                return (
                  <Fragment key={s.subscriptionId}>
                    <tr className={open ? 'row-selected' : undefined}>
                      <td>
                        <strong>{s.planName ?? 'Subscription'}</strong>
                        {s.pending && (
                          <div className="sub-meta">Switches to {s.pending.planName ?? 'a new plan'} on {fmtDate(s.currentPeriodEnd)}</div>
                        )}
                        {s.cancelAtPeriodEnd && (
                          <div className="sub-meta warn-text">Cancels {fmtDate(s.currentPeriodEnd)} — no refund</div>
                        )}
                      </td>
                      <td>
                        {s.billingMode === 'licensed'
                          ? `Per-device · ${s.quantity} seat${s.quantity === 1 ? '' : 's'}`
                          : 'Per transaction'}
                        {s.billingMode === 'licensed' && s.breakdown && (
                          <div className="sub-meta mono">{money(s.breakdown.total, s.breakdown.currency)}/{s.breakdown.interval}</div>
                        )}
                      </td>
                      <td>
                        {fmtDate(s.currentPeriodEnd) || '—'}
                        {/* The point of the row: what the next invoice actually becomes. */}
                        {renewal && (
                          <div className="sub-meta renewal-change">
                            then {money(renewal.amount, s.breakdown?.currency ?? 'usd')}/{renewal.interval}
                          </div>
                        )}
                      </td>
                      <td><span className={`status-dot ${STATUS_CLASS[s.status] ?? 'muted'}`}>{s.status}</span></td>
                      <td className="tx-actions">
                        {hasDetail && (
                          <button className="btn btn-ghost btn-sm" aria-expanded={open}
                            onClick={() => setExpanded(open ? null : s.subscriptionId)}>
                            {open ? 'Hide' : 'Manage'}
                          </button>
                        )}
                      </td>
                    </tr>

                    {open && (
                      <tr className="sub-detail-row">
                        <td colSpan={5}>
                          {s.billingMode === 'licensed' && s.breakdown && (
                            <div className="cost-breakdown">
                              <div className="cost-row">
                                <span>{s.planName ?? 'Plan'} <span className="cost-note">({s.breakdown.includedSeats} scanner{s.breakdown.includedSeats === 1 ? '' : 's'} included)</span></span>
                                <span className="mono">{money(s.breakdown.baseAmount, s.breakdown.currency)}/{s.breakdown.interval}</span>
                              </div>
                              {s.breakdown.additionalCount > 0 && (
                                <div className="cost-row">
                                  <span>Additional scanners: {s.breakdown.additionalCount} × {s.breakdown.additionalUnitAmount != null ? money(s.breakdown.additionalUnitAmount, s.breakdown.currency) : '—'}</span>
                                  <span className="mono">{s.breakdown.additionalUnitAmount != null ? money(s.breakdown.additionalCount * s.breakdown.additionalUnitAmount, s.breakdown.currency) : '—'}/{s.breakdown.interval}</span>
                                </div>
                              )}
                              <div className="cost-row cost-total">
                                <span>Total</span>
                                <span className="mono">{money(s.breakdown.total, s.breakdown.currency)}/{s.breakdown.interval}</span>
                              </div>
                              {renewal && (
                                <div className="cost-row cost-renewal">
                                  <span>
                                    From {fmtDate(s.currentPeriodEnd)}
                                    {renewal.note && <span className="cost-note"> ({renewal.note})</span>}
                                  </span>
                                  <span className="mono">{money(renewal.amount, s.breakdown.currency)}/{renewal.interval}</span>
                                </div>
                              )}

                              <div className="scanner-stepper">
                                <span className="scanner-label">Scanners: <b>{s.quantity}</b> ({s.breakdown.includedSeats} included + {s.breakdown.additionalCount} additional)</span>
                                <span className="stepper-controls">
                                  <button className="btn btn-ghost btn-sm" disabled={busy === 'scanners' || s.breakdown.additionalCount === 0}
                                    onClick={() => setScanners(s.breakdown!.additionalCount - 1)} aria-label="Remove a scanner">−</button>
                                  <button className="btn btn-primary btn-sm" disabled={busy === 'scanners'}
                                    onClick={() => setAddConfirm(s)}>
                                    {busy === 'scanners' ? '…' : '+ Add scanner'}
                                  </button>
                                </span>
                              </div>
                              {s.pendingAdditional != null && s.pendingAdditional !== s.breakdown.additionalCount && (
                                <div className="banner-warn" style={{ marginTop: 10 }}>
                                  Scheduled: additional scanners drop to <strong>{s.pendingAdditional}</strong> on {fmtDate(s.currentPeriodEnd)} (no mid-cycle credit).
                                </div>
                              )}
                              <div className="cost-note" style={{ marginTop: 8 }}>
                                Adding a scanner is charged straight away, prorated for the time left in this billing period; removing one takes effect at renewal (no mid-cycle credit).
                              </div>
                            </div>
                          )}

                          {s.cancelAtPeriodEnd ? (
                            <div className="banner-warn" style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                              <span>Cancels on {fmtDate(s.currentPeriodEnd)} — no refund for the current period.</span>
                              <button className="btn btn-ghost btn-sm" onClick={() => setCancel(s.subscriptionId, false)} disabled={busy === `cancel-${s.subscriptionId}`}>
                                Keep subscription
                              </button>
                            </div>
                          ) : (
                            <div className="sub-actions">
                              {targets.map((t) => {
                                const immediate = s.planType === 'metered' && t.planType === 'unlimited';
                                const key = `chg-${s.subscriptionId}-${t.priceId}`;
                                return (
                                  <button key={t.priceId} className="btn btn-ghost btn-sm" disabled={busy === key} onClick={() => changePlan(s.subscriptionId, t.priceId, key)}>
                                    {busy === key ? '…' : `${immediate ? 'Upgrade now' : 'Switch at renewal'}: ${t.productName} (${rateLabel(t)})`}
                                  </button>
                                );
                              })}
                              <button className="btn btn-ghost btn-sm cancel-btn" onClick={() => setCancel(s.subscriptionId, true)} disabled={busy === `cancel-${s.subscriptionId}`}>
                                Cancel plan
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="section-title" style={{ marginTop: 32 }}>Available plans</div>
      {plans.isLoading ? (
        <p className="loading-screen">// loading plans…</p>
      ) : availablePlans.length === 0 ? (
        <div className="panel empty"><h3>You're all set</h3><p>You're subscribed to the available plans. Use the actions above to change or cancel.</p></div>
      ) : (
        <div className="plan-grid">
          {availablePlans.map((p) => (
            <div key={p.priceId} className="plan-card">
              <div className="plan-top">
                <h3>{p.productName}</h3>
                <span className={`pill ${p.usageType === 'metered' ? 'pill-doc' : 'pill-scan'}`}>
                  {p.usageType === 'metered' ? 'Metered' : 'Per-device'}
                </span>
              </div>
              {p.description && <p className="plan-desc">{p.description}</p>}
              {/* Usage pricing has no recurring figure to quote — "/month" beside a per-transaction
                  rate reads as a monthly fee, which these plans don't charge. */}
              <div className="plan-price">
                {p.usageType === 'metered' ? (
                  <>
                    {p.tiers && <span className="plan-from">starting at </span>}
                    {money(p.amount, p.currency)}
                    <span className="plan-interval">/tx</span>
                  </>
                ) : (
                  <>
                    {money(p.amount, p.currency)}
                    <span className="plan-interval">/{p.interval}</span>
                  </>
                )}
              </div>
              {p.usageType !== 'metered' && <DeviceNote plan={p} />}
              {p.tiers && p.tiers.length > 1 && <TierTable tiers={p.tiers} currency={p.currency} />}
              <button className="btn btn-primary" onClick={() => subscribe(p.priceId)} disabled={busy === p.priceId}>
                {busy === p.priceId ? 'Starting…' : 'Subscribe →'}
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="cart-note" style={{ textAlign: 'left', marginTop: 20 }}>
        Plan switches and downgrades take effect at the end of your billing cycle; moving from pay-per-transaction to
        an unlimited plan takes effect immediately. Cancellations apply at period end with no refund.
      </p>

      <CardConversionCredits />

      <StorageTiers />

      <ConfirmDialog
        open={addConfirm != null}
        title="Add a scanner license?"
        confirmLabel="Add scanner"
        busy={busy === 'scanners'}
        onCancel={() => setAddConfirm(null)}
        onConfirm={async () => {
          const s = addConfirm;
          if (!s?.breakdown) return;
          await setScanners(s.breakdown.additionalCount + 1);
          setAddConfirm(null);
        }}
      >
        {addConfirm?.breakdown && (
          <>
            <p>
              This adds one licensed scanner to <strong>{addConfirm.planName ?? 'your plan'}</strong> at{' '}
              <strong>
                {addConfirm.breakdown.additionalUnitAmount != null
                  ? money(addConfirm.breakdown.additionalUnitAmount, addConfirm.breakdown.currency)
                  : 'the additional-scanner rate'}
                /{addConfirm.breakdown.interval}
              </strong>.
            </p>
            <p className="confirm-policy">
              <strong>Cancellation policy:</strong> the added scanner is charged <strong>prorated immediately</strong> for
              the remainder of your current billing period. You can remove a scanner at any time, but removals take
              effect at the <strong>end of the billing period</strong> — there is <strong>no mid-cycle credit or
              refund</strong> — and you can't reduce below the number of scanners currently assigned to a device.
            </p>
          </>
        )}
      </ConfirmDialog>
    </div>
  );
}
